import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

interface SidebarContextType {
  isCollapsed: boolean;
  isTransitioning: boolean;
  isMobileOpen: boolean;
  toggleSidebar: () => void;
  collapseSidebar: () => void;
  expandSidebar: () => void;
  setMobileOpen: (open: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

interface SidebarProviderProps {
  children: ReactNode;
}

const TRANSITION_DURATION = 750; // ms - matches route animation duration

const isAutoCollapsePath = (pathname: string) => /^\/(research|vault-document)\/[^/]+$/.test(pathname);

export const SidebarProvider: React.FC<SidebarProviderProps> = ({ children }) => {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(() => isAutoCollapsePath(location.pathname));
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobileOpen, setMobileOpen] = useState(false);
  const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousPathnameRef = useRef<string>(location.pathname);
  const isManualToggleRef = useRef<boolean>(false);

  useEffect(() => {
    const shouldCollapse = isAutoCollapsePath(location.pathname);

    // Only update if route actually changed (not just state update)
    const routeChanged = previousPathnameRef.current !== location.pathname;
    previousPathnameRef.current = location.pathname;

    // Skip if this was a manual toggle
    if (isManualToggleRef.current) {
      isManualToggleRef.current = false;
      return;
    }

    // Only trigger transition if route changed and state needs to change
    if (routeChanged && shouldCollapse !== isCollapsed) {
      setIsTransitioning(true);

      // Clear any existing timeout
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }

      // Update collapsed state
      setIsCollapsed(shouldCollapse);

      // Mark transition as complete after animation duration
      transitionTimeoutRef.current = setTimeout(() => {
        setIsTransitioning(false);
      }, TRANSITION_DURATION);
    }

    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, [location.pathname, location.key, isCollapsed]);

  const toggleSidebar = () => {
    isManualToggleRef.current = true;
    setIsTransitioning(true);
    setIsCollapsed((prev) => !prev);

    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
    }

    transitionTimeoutRef.current = setTimeout(() => {
      setIsTransitioning(false);
    }, TRANSITION_DURATION);
  };

  const collapseSidebar = () => {
    if (!isCollapsed) {
      setIsTransitioning(true);
      setIsCollapsed(true);

      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }

      transitionTimeoutRef.current = setTimeout(() => {
        setIsTransitioning(false);
      }, TRANSITION_DURATION);
    }
  };

  const expandSidebar = () => {
    if (isCollapsed) {
      setIsTransitioning(true);
      setIsCollapsed(false);

      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }

      transitionTimeoutRef.current = setTimeout(() => {
        setIsTransitioning(false);
      }, TRANSITION_DURATION);
    }
  };

  const value: SidebarContextType = {
    isCollapsed,
    isTransitioning,
    isMobileOpen,
    toggleSidebar,
    collapseSidebar,
    expandSidebar,
    setMobileOpen,
  };

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
};
